/**
 * 移动端最近购物车列表页
 * 展示最近更新的购物车（产品、用户名、账号）
 */
import React, { useEffect, useState } from 'react';
import {
  List,
  PullToRefresh,
  Empty,
  Skeleton,
  InfiniteScroll,
  Tag,
  SearchBar,
} from 'antd-mobile';
import { adminCartApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';

export default function MobileRecentCartListPage() {
  const [loading, setLoading] = useState(true);
  const [carts, setCarts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  useEffect(() => {
    fetchCarts(1);
  }, [searchKeyword]);

  const fetchCarts = async (pageNum, append = false) => {
    try {
      const params = {
        page: pageNum,
        pageSize: 10,
        keyword: searchKeyword || undefined,
      };
      const data = await adminCartApi.listRecent(params);
      const newCarts = data?.items || data?.list || data || [];
      if (append) {
        setCarts((prev) => [...prev, ...newCarts]);
      } else {
        setCarts(newCarts);
      }
      setHasMore(newCarts.length >= 10);
      setPage(pageNum);
    } catch (err) {
      Toast.show('获取购物车列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchKeyword(keyword);
  };

  const loadMore = async () => {
    if (!hasMore) return;
    await fetchCarts(page + 1, true);
  };

  const refreshData = async () => {
    await fetchCarts(1, false);
  };

  const formatTime = (t) => {
    if (!t) return '-';
    const d = new Date(t);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  if (loading && carts.length === 0) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton.Title rows={3} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: 16 }}>
      {/* 搜索栏 */}
      <div style={{ background: '#fff', padding: '6px 12px', marginBottom: 12, borderRadius: 8 }}>
        <SearchBar
          value={keyword}
          onChange={setKeyword}
          onSearch={handleSearch}
          placeholder="搜索邮箱或用户名"
          style={{ '--background': '#f5f5f5' }}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>最近购物车</h2>
        <p style={{ fontSize: 13, color: '#999' }}>按最后更新时间排序</p>
      </div>

      <PullToRefresh onRefresh={refreshData}>
        {carts.length > 0 ? (
          <List>
            {carts.map((cart) => (
              <List.Item
                key={cart.id}
                style={{
                  background: '#fff',
                  marginBottom: 12,
                  padding: '12px 16px',
                  borderRadius: 8,
                }}
              >
                {/* 顶部：账号 + 商品数 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {cart.userEmail || (
                        <Tag color="default" fill="outline">
                          游客 #{cart.sessionId?.slice(0, 8) || '-'}
                        </Tag>
                      )}
                    </div>
                    {cart.userName && (
                      <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                        {cart.userName}
                      </div>
                    )}
                  </div>
                  <Tag color="primary" fill="outline">
                    {cart.itemCount || 0} 件
                  </Tag>
                </div>

                {/* 产品列表 */}
                {cart.items && cart.items.length > 0 && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #eee' }}>
                    {cart.items.map((it) => (
                      <div
                        key={it.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 6,
                        }}
                      >
                        {it.imageUrl && (
                          <img
                            src={it.imageUrl}
                            alt={it.productTitle}
                            style={{
                              width: 36,
                              height: 36,
                              objectFit: 'cover',
                              borderRadius: 4,
                              border: '1px solid #f0f0f0',
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              color: '#333',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {it.productTitle}
                            {it.variantTitle && it.variantTitle !== 'Default Title' && (
                              <span style={{ color: '#999', marginLeft: 4 }}>
                                ({it.variantTitle})
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#999' }}>
                            ¥{(it.price || 0).toFixed(2)} × {it.quantity}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 底部：时间 + 小计 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: '1px solid #f5f5f5',
                  }}
                >
                  <span style={{ fontSize: 12, color: '#999' }}>
                    更新于 {formatTime(cart.updatedAt)}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#f5222d' }}>
                    ¥{(cart.subtotal || 0).toFixed(2)}
                  </span>
                </div>
              </List.Item>
            ))}
          </List>
        ) : (
          <div style={{ padding: '80px 0' }}>
            <Empty description="暂无购物车数据" />
          </div>
        )}
      </PullToRefresh>

      {carts.length > 0 && hasMore && (
        <InfiniteScroll loadMore={loadMore} style={{ paddingBottom: 60 }} />
      )}
    </div>
  );
}
